import api from '../client';

/**
 * Service pour la gestion des factures (piloté + copiloté).
 * Même entité côté back, qu'elle soit créée par l'agent IA (status: draft)
 * ou à la main par l'utilisateur depuis l'interface.
 */
const invoiceService = {
  /**
   * Récupérer la liste des factures
   * @param {Object} params - Paramètres (status, page, limit, search)
   * @returns {Promise} Liste des factures avec pagination
   */
  getInvoices: async (params = {}) => {
    try {
      const response = await api.get('/invoices', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer une facture par son ID
   * @param {string} id - ID de la facture
   * @returns {Promise} Données de la facture
   */
  getInvoiceById: async (id) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer une nouvelle facture (manuel)
   * @param {Object} invoiceData - Données de la facture
   * @returns {Promise} Facture créée
   */
  createInvoice: async (invoiceData) => {
    try {
      const response = await api.post('/invoices', invoiceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Générer une facture à partir d'une commande
   * @param {string} orderId - ID de la commande
   * @returns {Promise} Facture (brouillon) générée
   */
  createFromOrder: async (orderId) => {
    try {
      const response = await api.post(`/invoices/from-order/${orderId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Éditer un brouillon de facture (draft uniquement)
   * @param {string} id - ID de la facture
   * @param {Object} invoiceData - Nouvelles données
   * @returns {Promise} Facture mise à jour
   */
  updateInvoice: async (id, invoiceData) => {
    try {
      const response = await api.patch(`/invoices/${id}`, invoiceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Envoyer une facture (draft → sent, génère le PDF + le lien de paiement)
   * @param {string} id - ID de la facture
   * @returns {Promise} Facture envoyée
   */
  sendInvoice: async (id) => {
    try {
      const response = await api.post(`/invoices/${id}/send`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Marquer une facture comme payée
   * @param {string} id - ID de la facture
   * @returns {Promise} Facture mise à jour
   */
  markPaid: async (id) => {
    try {
      const response = await api.post(`/invoices/${id}/mark-paid`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Annuler une facture
   * @param {string} id - ID de la facture
   * @returns {Promise} Facture annulée
   */
  cancelInvoice: async (id) => {
    try {
      const response = await api.post(`/invoices/${id}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer le modèle de facture global de l'organisation
   * (logo, modèle, couleur, coordonnées société).
   * @returns {Promise} { success, data: settings }
   */
  getSettings: async () => {
    try {
      const response = await api.get('/invoices/settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Enregistrer le modèle de facture global de l'organisation.
   * S'applique à TOUTES les factures (PDF généré côté backend).
   * @param {Object} settings - { templateId, brandColor, logoUrl, company:{name,address,phone,email} }
   * @returns {Promise} { success, data: settings }
   */
  saveSettings: async (settings) => {
    try {
      const response = await api.put('/invoices/settings', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer le PDF de la facture (blob).
   * GET /invoices/:id/pdf nécessite l'en-tête Authorization, on ne peut donc pas
   * ouvrir l'URL directement dans un onglet — on récupère le blob via le client
   * authentifié puis on génère une URL objet à ouvrir.
   * @param {string} id - ID de la facture
   * @returns {Promise<Blob>} Contenu PDF
   */
  getInvoicePdf: async (id) => {
    try {
      const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default invoiceService;
